import { renderButton } from 'BomdisoftComponents/buttons/render';
import { renderStatus } from 'BomdisoftComponents/chips/render';
import { attributes, html } from 'BomdisoftComponents/html/index';
import { renderTextField } from 'BomdisoftComponents/inputs/field/render';

export type EntryListOwnState = 'covered' | 'listed' | 'missing';

export type EntryListOwn = {
  entry: string;
  state: EntryListOwnState;
  tag: string;
};

export type EntryListOptions = {
  addAriaLabel: (entry: string) => string;
  addLabel: string;
  entries: readonly string[];
  fieldError?: boolean;
  fieldHelperId?: string;
  fieldHelperText?: string;
  fieldId: string;
  fieldLabel: string;
  fieldValue: string;
  isFull?: boolean;
  own?: EntryListOwn;
  removeAriaLabel: (entry: string) => string;
  removeLabel: string;
  testId?: string;
};

export const toListedEntries = (entries: readonly string[], own: EntryListOwn | undefined) => (own?.state === 'listed' ? entries.filter(entry => entry !== own.entry) : entries);

export const canAddEntry = (entries: readonly string[], value: string, isFull: boolean) => !isFull && value.trim() !== '' && !entries.includes(value.trim());

const testIdOf = (testId: string | undefined, part: string) => (testId ? `${testId}-${part}` : undefined);

const renderOwnAction = ({ addAriaLabel, addLabel, isFull = false, own, removeAriaLabel, removeLabel, testId }: EntryListOptions & { own: EntryListOwn }) => {
  if (own.state === 'covered') return '';
  if (own.state === 'listed') return renderButton({ attributes: { 'aria-label': removeAriaLabel(own.entry), 'data-entry-action': 'remove', 'data-testid': testIdOf(testId, 'own-remove') }, children: removeLabel, color: 'error', size: 'small' });
  return renderButton({ attributes: { 'aria-label': addAriaLabel(own.entry), 'data-entry-action': 'add', 'data-testid': testIdOf(testId, 'own-add'), disabled: isFull }, children: addLabel, size: 'small' });
};

export const renderEntryList = (options: EntryListOptions) => {
  const { addLabel, entries, fieldError, fieldHelperId, fieldHelperText, fieldId, fieldLabel, fieldValue, isFull = false, own, removeAriaLabel, removeLabel, testId } = options;
  const ownRow =
    own === undefined
      ? ''
      : html`<li${attributes({ class: 'bomdisoft-entry-list-item', 'data-entry': own.entry, 'data-own': own.state, 'data-testid': testIdOf(testId, 'own') })}><span class="bomdisoft-entry-list-name">${own.entry} ${renderStatus({ capitalize: false, size: 'small', text: own.tag, variant: 'info' })}</span>${renderOwnAction({ ...options, own })}</li>`;
  const rows = toListedEntries(entries, own).map(
    entry =>
      html`<li${attributes({ class: 'bomdisoft-entry-list-item', 'data-entry': entry, 'data-testid': testIdOf(testId, `row-${entry}`) })}><span class="bomdisoft-entry-list-name">${entry}</span>${renderButton({ attributes: { 'aria-label': removeAriaLabel(entry), 'data-entry-action': 'remove', 'data-testid': testIdOf(testId, `remove-${entry}`) }, children: removeLabel, color: 'error', size: 'small' })}</li>`,
  );
  const field = renderTextField({ attributes: { 'data-testid': testIdOf(testId, 'field') }, disabled: isFull, error: fieldError, helperId: fieldHelperText ? (fieldHelperId ?? `${fieldId}-helper`) : undefined, helperText: fieldHelperText, id: fieldId, label: fieldLabel, value: fieldValue });
  const add = renderButton({ attributes: { 'data-entry-action': 'add', 'data-testid': testIdOf(testId, 'add'), disabled: !canAddEntry(entries, fieldValue, isFull) }, children: addLabel, size: 'small', variant: 'outlined' });
  return html`<div${attributes({ class: 'bomdisoft-entry-list', 'data-testid': testId })}><ul class="bomdisoft-entry-list-items">${ownRow}${rows}</ul><div class="bomdisoft-entry-list-adder">${field}${add}</div></div>`;
};
